"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { graphql } from "gql.tada";
import { usePathname } from "next/navigation";
import { useQueryStates } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { memoDateFilterSearchParamParsers } from "@/app/[lang]/[workspaceId]/_search-params/memo-date-filter";
import { useToast } from "@/hooks/use-toast";
import { DASHBOARD_POLICIES } from "@/lib/authorization/policies/dashboard";
import { IW_POLICIES } from "@/lib/authorization/policies/index-worker";
import { SA_POLICIES } from "@/lib/authorization/policies/search-analytics";
import { UA_POLICIES } from "@/lib/authorization/policies/user-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import {
  dispatchMemoChanged,
  dispatchMemoHovered,
  type MemoPublicationScope,
  subscribeMemoHovered,
  useMemoDisplaySettings,
  useMemoRelatedContexts,
  useMemoVisibleDateRange,
  writeMemoDisplaySettings,
} from "../memos/memo-events";
import { DEFAULT_MEMO_COLOR } from "../memos/user-memo-colors";
import {
  type UserMemoCreateTargetOption,
  useUserMemoCreateTargetOptions,
} from "../memos/user-memo-create-target-options";
import { useAiSidePanelVisibility } from "./ai-side-panel-visibility";
import {
  type MemoContextKeyInput,
  memoContextKey,
  memoListCardHostFromPathname,
  memoTargetFromPathname,
} from "./memo-node-id";
import {
  authorsFromMemosForFilter,
  isMemoNode,
  memoFormDateModeFromDates,
  memoFormErrorMessage,
  mergePanelOptimisticMemos,
  normalizeMemoDateInput,
  panelDateFilterRange,
  userMemosMatchingPanelDerivedFilters,
} from "./memo-panel-filter";
import {
  DEFAULT_FILTER,
  formStateFromIntent,
  formStateFromMemo,
  type MemoFormState,
  type MemoPanelMemo,
  memoContextFromUserMemoContext,
  memoContextInput,
  memoSourceFilterValueForSystemLabelKey,
  type PanelFilter,
  SYSTEM_MEMO_SOURCE_FILTER_VALUE,
  type SystemMemoLabelFilterOption,
  type UserMemoFeatureKind,
} from "./memo-panel-types";
import {
  type MemoPanelIntent,
  type UserMemoContext,
  useWorkspaceSidePanelMode,
} from "./workspace-side-panel-mode";

const MemoPanelQuery = graphql(`
  query MemoPanel(
    $context: MemoContextInput!
    $filter: MemoFilterInput
    $orderBy: MemoOrder!
    $locale: String
  ) {
    memos(
      context: $context
      filter: $filter
      orderBy: $orderBy
      locale: $locale
    ) {
      __typename
      id
      memoType
      publicationScope
      featureKind
      label
      body
      color
      dateMode
      startDate
      endDate
      sourceLabel
      sourceUrl
      createdAt
      updatedAt
      ... on UserMemo {
        originTargetKind
        originTargetID
        deletedAt
        createdBy {
          id
          name
          picture
        }
        updatedBy {
          id
          name
          picture
        }
        deletedBy {
          id
          name
          picture
        }
        canEdit
        canDelete
        canRestore
      }
      ... on SystemMemo {
        provider
        externalID
        systemLabels: labels {
          key
          name
          color
          localizedTexts {
            locale
            name
          }
        }
        links {
          url
          label
          kind
          primary
        }
      }
    }
  }
`);

const UserMemoSystemMemoLabelsQuery = graphql(`
  query UserMemoSystemMemoLabels {
    systemMemoLabels(filter: { includeArchived: true }) {
      key
      name
      color
      localizedTexts {
        locale
        name
      }
    }
  }
`);

const CreateUserMemoMutation = graphql(`
  mutation CreateUserMemoPanel($input: CreateUserMemoInput!) {
    createUserMemo(input: $input) {
      __typename
      id
      memoType
      publicationScope
      featureKind
      label
      body
      color
      dateMode
      startDate
      endDate
      sourceLabel
      sourceUrl
      originTargetKind
      originTargetID
      deletedAt
      createdAt
      updatedAt
      createdBy {
        id
        name
        picture
      }
      updatedBy {
        id
        name
        picture
      }
      deletedBy {
        id
        name
        picture
      }
      canEdit
      canDelete
      canRestore
    }
  }
`);

const UpdateUserMemoMutation = graphql(`
  mutation UpdateUserMemoPanel($input: UpdateUserMemoInput!) {
    updateUserMemo(input: $input) {
      __typename
      id
      memoType
      publicationScope
      featureKind
      label
      body
      color
      dateMode
      startDate
      endDate
      sourceLabel
      sourceUrl
      originTargetKind
      originTargetID
      deletedAt
      createdAt
      updatedAt
      createdBy {
        id
        name
        picture
      }
      updatedBy {
        id
        name
        picture
      }
      deletedBy {
        id
        name
        picture
      }
      canEdit
      canDelete
      canRestore
    }
  }
`);

const DeleteUserMemoMutation = graphql(`
  mutation DeleteUserMemoPanel($input: DeleteUserMemoInput!) {
    deleteUserMemo(input: $input) {
      id
    }
  }
`);

const RestoreUserMemoMutation = graphql(`
  mutation RestoreUserMemoPanel($input: RestoreUserMemoInput!) {
    restoreUserMemo(input: $input) {
      __typename
      id
      memoType
      publicationScope
      featureKind
      label
      body
      color
      dateMode
      startDate
      endDate
      sourceLabel
      sourceUrl
      originTargetKind
      originTargetID
      deletedAt
      createdAt
      updatedAt
      createdBy {
        id
        name
        picture
      }
      updatedBy {
        id
        name
        picture
      }
      deletedBy {
        id
        name
        picture
      }
      canEdit
      canDelete
      canRestore
    }
  }
`);

/** memo の通常編集に必要な feature 別 policy を返します。 */
function userMemoEditPolicyForFeatureKind(featureKind: UserMemoFeatureKind) {
  switch (featureKind) {
    case "DASHBOARD":
      return DASHBOARD_POLICIES["dashboard:edit"];
    case "INDEX_WORKER":
      return IW_POLICIES["iw-project:edit"];
    case "USER_ANALYTICS":
      return UA_POLICIES["ua-view:edit"];
    case "SEARCH_ANALYTICS":
      return SA_POLICIES["sa-view:edit"];
  }
}

type OwnedMemoFormDraft = {
  ownerKey: string;
  form: MemoFormState;
};

type TargetScopedOptimisticMemos = {
  contextKey: string | null;
  memosById: ReadonlyMap<string, MemoPanelMemo>;
};

const EMPTY_OPTIMISTIC_MEMOS: ReadonlyMap<string, MemoPanelMemo> = new Map();

function compareMemoPanelMemosByDateDesc(a: MemoPanelMemo, b: MemoPanelMemo) {
  const start = (b.startDate ?? "").localeCompare(a.startDate ?? "");
  if (start !== 0) return start;
  const end = (b.endDate ?? "").localeCompare(a.endDate ?? "");
  if (end !== 0) return end;
  return a.id.localeCompare(b.id);
}

function relatedSystemMemoContextsFromIntent(
  intent: MemoPanelIntent | null,
  publishedRelatedContexts: readonly UserMemoContext[],
) {
  if (intent?.state !== "list") return [];

  const contextsByFeatureKind = new Map<string, UserMemoContext>();
  for (const relatedContext of [
    ...(intent.relatedContexts ?? []),
    ...publishedRelatedContexts,
  ]) {
    if (relatedContext.featureKind === "DASHBOARD") continue;
    if (contextsByFeatureKind.has(relatedContext.featureKind)) continue;
    contextsByFeatureKind.set(relatedContext.featureKind, relatedContext);
  }
  return Array.from(contextsByFeatureKind.values());
}

function emptyMemoQueryContext() {
  return {
    workspaceID: "",
    featureKind: "SEARCH_ANALYTICS" as const,
    projectID: null,
    targetKind: "SEARCH_ANALYTICS_VIEW" as const,
    targetID: null,
  };
}

function routeAnchorContextForIntent(intent: MemoPanelIntent | null) {
  if (!intent) return null;
  if (intent.state === "detail" && intent.returnList) {
    return intent.returnList.context;
  }
  return intent.context;
}

function useRelatedSystemMemoPanelMemos({
  panelContext,
  relatedContexts,
  queryFilter,
  locale,
}: {
  panelContext: UserMemoContext | undefined;
  relatedContexts: readonly UserMemoContext[];
  queryFilter: {
    deleted: PanelFilter["deleted"];
    visibleDateRange: ReturnType<typeof panelDateFilterRange>;
  };
  locale: string;
}) {
  const context0 = relatedContexts[0] ?? null;
  const context1 = relatedContexts[1] ?? null;
  const context2 = relatedContexts[2] ?? null;
  const systemQueryFilter = useMemo(
    () => ({
      ...queryFilter,
      memoTypes: ["SYSTEM" as const],
    }),
    [queryFilter],
  );
  const queryContext0 = useMemo(
    () =>
      context0
        ? memoContextFromUserMemoContext(context0)
        : emptyMemoQueryContext(),
    [context0],
  );
  const queryContext1 = useMemo(
    () =>
      context1
        ? memoContextFromUserMemoContext(context1)
        : emptyMemoQueryContext(),
    [context1],
  );
  const queryContext2 = useMemo(
    () =>
      context2
        ? memoContextFromUserMemoContext(context2)
        : emptyMemoQueryContext(),
    [context2],
  );

  const [{ data: data0, fetching: fetching0 }, reexecute0] = useQuery({
    query: MemoPanelQuery,
    variables: {
      context: queryContext0,
      filter: systemQueryFilter,
      orderBy: "DATE_ASC" as const,
      locale,
    },
    pause: !panelContext || !context0,
  });
  const [{ data: data1, fetching: fetching1 }, reexecute1] = useQuery({
    query: MemoPanelQuery,
    variables: {
      context: queryContext1,
      filter: systemQueryFilter,
      orderBy: "DATE_ASC" as const,
      locale,
    },
    pause: !panelContext || !context1,
  });
  const [{ data: data2, fetching: fetching2 }, reexecute2] = useQuery({
    query: MemoPanelQuery,
    variables: {
      context: queryContext2,
      filter: systemQueryFilter,
      orderBy: "DATE_ASC" as const,
      locale,
    },
    pause: !panelContext || !context2,
  });

  const slots = useMemo(
    () =>
      [
        { context: context0, data: data0, reexecute: reexecute0 },
        { context: context1, data: data1, reexecute: reexecute1 },
        { context: context2, data: data2, reexecute: reexecute2 },
      ] as const,
    [
      context0,
      context1,
      context2,
      data0,
      data1,
      data2,
      reexecute0,
      reexecute1,
      reexecute2,
    ],
  );
  const memos = useMemo(
    () =>
      slots.flatMap((slot) =>
        slot.context ? (slot.data?.memos ?? []).filter(isMemoNode) : [],
      ),
    [slots],
  );
  const memoContextById = useMemo(() => {
    const map = new Map<string, UserMemoContext>();
    for (const slot of slots) {
      if (!slot.context) continue;
      for (const memo of slot.data?.memos ?? []) {
        if (!isMemoNode(memo)) continue;
        map.set(memo.id, slot.context);
      }
    }
    return map;
  }, [slots]);
  const refresh = useCallback(() => {
    for (const slot of slots) {
      if (!slot.context) continue;
      slot.reexecute({ requestPolicy: "cache-and-network" });
    }
  }, [slots]);

  return {
    memos,
    memoContextById,
    fetching: fetching0 || fetching1 || fetching2,
    refresh,
  };
}

/**
 * form draft の所有対象を表す key を返します。
 * detail / edit 間の表示切替では同じ memo の draft を維持し、別 memo / 新規作成へ移った時だけ reset します。
 */
function memoFormDraftOwnerKeyFromIntent(intent: MemoPanelIntent) {
  switch (intent.state) {
    case "create":
      return [
        "create",
        memoContextKey(intent.context),
        intent.initialDate ?? "",
        intent.source ?? "panel",
      ].join(":");
    case "detail":
    case "edit":
      return `memo:${intent.memoId}`;
    case "list":
      return null;
  }
}

/** 保存済み memo の mutation 結果に対応する draft owner key を返します。 */
function memoFormDraftOwnerKeyFromMemoId(memoId: string) {
  return `memo:${memoId}`;
}

/**
 * 保存済み memo の origin target を、一覧画面が公開した選択肢の key へ戻します。
 * VIEW scope へ変更する編集でも、元の作成元 view を初期選択として示すために使います。
 */
function memoTargetOptionKeyFromMemo(
  memo: MemoPanelMemo | null,
  fallbackContext: MemoContextKeyInput,
  options: readonly UserMemoCreateTargetOption[],
) {
  if (memo?.originTargetKind && memo.originTargetID) {
    const option = options.find(
      (candidate) =>
        candidate.context.targetKind === memo.originTargetKind &&
        candidate.context.targetId === memo.originTargetID,
    );
    if (option) {
      return option.key;
    }
  }
  return memoContextKey(fallbackContext);
}

/** URL の locale segment を memo の表示言語として使える形で返します。 */
function memoLocaleFromPathname(pathname: string | null) {
  const locale = pathname?.split("/").filter(Boolean)[0];
  return locale === "ja" || locale === "en" ? locale : null;
}

/**
 * User memo side panel の data loading、mutation、intent transition をまとめます。
 * 描画 component から urql と URL query 同期を分離し、form/list/detail は props だけで表現できる状態にします。
 */
export function useMemoPanelController() {
  const pathname = usePathname();
  const { i18n } = useLingui();
  const { toast } = useToast();
  const checkPolicy = useCheckPolicy();
  const { isVisible: isWorkspaceSidePanelVisible } = useAiSidePanelVisibility();
  const { mode, userMemoIntent, openUserMemo, closeWorkspaceSidePanel } =
    useWorkspaceSidePanelMode();
  const [dateFilterQuery, setDateFilterQuery] = useQueryStates(
    memoDateFilterSearchParamParsers,
  );
  const [filter, setFilter] = useState<PanelFilter>(DEFAULT_FILTER);
  const [draftForm, setDraftForm] = useState<OwnedMemoFormDraft | null>(() =>
    userMemoIntent && userMemoIntent.state === "create"
      ? {
          ownerKey: memoFormDraftOwnerKeyFromIntent(userMemoIntent) ?? "",
          form: formStateFromIntent(userMemoIntent, DEFAULT_MEMO_COLOR),
        }
      : null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [chartHoveredMemoIds, setChartHoveredMemoIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [listHoveredMemoId, setListHoveredMemoId] = useState<string | null>(
    null,
  );
  const memoDisplaySettings = useMemoDisplaySettings();
  const [optimisticMemos, setOptimisticMemos] =
    useState<TargetScopedOptimisticMemos>(() => ({
      contextKey: null,
      memosById: EMPTY_OPTIMISTIC_MEMOS,
    }));
  const [isSavingMemo, setIsSavingMemo] = useState(false);
  const memoTarget = useMemo(
    () => memoTargetFromPathname(pathname),
    [pathname],
  );
  const memoLocale = memoLocaleFromPathname(pathname) ?? i18n.locale;
  const listCardHostRoute = useMemo(
    () => memoListCardHostFromPathname(pathname),
    [pathname],
  );
  const createTargetOptions = useUserMemoCreateTargetOptions(listCardHostRoute);
  const context = userMemoIntent?.context;
  const publishedRelatedContexts = useMemoRelatedContexts(context ?? null);
  const routeVisibleDateRange = useMemoVisibleDateRange(memoTarget);
  const intentVisibleDateRange = useMemoVisibleDateRange(context ?? null);
  const explicitDateStart =
    dateFilterQuery.memoDateStart ?? dateFilterQuery.memoDate ?? "";
  const explicitDateEnd = dateFilterQuery.memoDateEnd ?? "";
  const currentDraftOwnerKey = userMemoIntent
    ? memoFormDraftOwnerKeyFromIntent(userMemoIntent)
    : null;
  const activeDraftForm =
    draftForm?.ownerKey === currentDraftOwnerKey ? draftForm.form : null;

  useEffect(() => {
    if (!userMemoIntent || !currentDraftOwnerKey) {
      setDraftForm(null);
      setFormError(null);
      return;
    }

    if (userMemoIntent.state === "create") {
      setDraftForm((current) =>
        current?.ownerKey === currentDraftOwnerKey
          ? current
          : {
              ownerKey: currentDraftOwnerKey,
              form: formStateFromIntent(userMemoIntent, DEFAULT_MEMO_COLOR),
            },
      );
      setFormError(null);
      return;
    }

    setDraftForm((current) =>
      current?.ownerKey === currentDraftOwnerKey ? current : null,
    );
    setFormError(null);
  }, [currentDraftOwnerKey, userMemoIntent]);

  const hasExplicitListIntentDate =
    userMemoIntent?.state === "list" &&
    Boolean(
      userMemoIntent.date || userMemoIntent.dateStart || userMemoIntent.dateEnd,
    );
  const listIntentDateStart = hasExplicitListIntentDate
    ? (userMemoIntent.dateStart ?? userMemoIntent.date ?? null)
    : undefined;
  const listIntentDateEnd = hasExplicitListIntentDate
    ? (userMemoIntent.dateEnd ?? null)
    : undefined;
  const currentRouteMemoTargetKey = memoTarget
    ? memoContextKey(memoTarget)
    : null;
  const routeAnchorContext = routeAnchorContextForIntent(
    userMemoIntent ?? null,
  );
  const currentRouteAnchorKey = routeAnchorContext
    ? memoContextKey(routeAnchorContext)
    : null;
  const currentIntentMemoTargetKey = userMemoIntent?.context
    ? memoContextKey(userMemoIntent.context)
    : null;
  const currentOptimisticMemoContextKey = context
    ? memoContextKey(context)
    : null;
  // board/list の card 起点 intent は page-level route target で上書きせず、card context を保持します。
  const isHostedCardMemoIntent =
    userMemoIntent !== null &&
    routeAnchorContext !== null &&
    listCardHostRoute !== null &&
    routeAnchorContext.workspaceId === listCardHostRoute.workspaceId &&
    routeAnchorContext.projectId === listCardHostRoute.projectId &&
    routeAnchorContext.featureKind === listCardHostRoute.featureKind;

  useEffect(() => {
    if (mode !== "MEMO" || !isWorkspaceSidePanelVisible) return;
    if (isHostedCardMemoIntent) return;
    if (!memoTarget) {
      closeWorkspaceSidePanel();
      return;
    }
    if (currentRouteAnchorKey === currentRouteMemoTargetKey) return;
    openUserMemo({
      state: "list",
      context: {
        workspaceId: memoTarget.workspaceId,
        featureKind: memoTarget.featureKind,
        targetKind: memoTarget.targetKind,
        targetId: memoTarget.targetId,
        projectId: memoTarget.projectId,
        searchAnalyticsId: "",
        viewName: "",
      },
    });
  }, [
    closeWorkspaceSidePanel,
    currentRouteAnchorKey,
    currentRouteMemoTargetKey,
    isHostedCardMemoIntent,
    isWorkspaceSidePanelVisible,
    memoTarget,
    mode,
    openUserMemo,
  ]);

  useEffect(() => {
    if (!context) {
      setChartHoveredMemoIds(new Set());
      return;
    }

    return subscribeMemoHovered(context, (detail) => {
      if (detail.source !== "chart") return;
      setChartHoveredMemoIds(new Set(detail.memoIds));
    });
  }, [context]);

  useEffect(() => {
    if (listIntentDateStart === undefined) return;
    void setDateFilterQuery(
      listIntentDateStart
        ? {
            memoDate: null,
            memoDateStart: listIntentDateStart,
            memoDateEnd: listIntentDateEnd,
          }
        : {
            memoDate: null,
            memoDateStart: null,
            memoDateEnd: null,
          },
    );
  }, [listIntentDateEnd, listIntentDateStart, setDateFilterQuery]);

  const isVisibleMemoListIntent =
    mode === "MEMO" &&
    isWorkspaceSidePanelVisible &&
    userMemoIntent?.state === "list" &&
    currentIntentMemoTargetKey !== null;

  const shouldUseVisibleDefaultDateRange =
    isVisibleMemoListIntent && !hasExplicitListIntentDate && !explicitDateStart;
  const visibleDefaultDateRange =
    intentVisibleDateRange ?? routeVisibleDateRange;
  const activeDateStart = shouldUseVisibleDefaultDateRange
    ? (visibleDefaultDateRange?.startDate ?? "")
    : explicitDateStart;
  const activeDateEnd = shouldUseVisibleDefaultDateRange
    ? (visibleDefaultDateRange?.endDate ?? "")
    : explicitDateEnd;
  const effectiveFilter = useMemo<PanelFilter>(
    () => ({
      ...filter,
      dateStart: activeDateStart,
      dateEnd: activeDateEnd,
    }),
    [activeDateEnd, activeDateStart, filter],
  );
  const visibleFilter = useMemo<PanelFilter>(
    () => ({
      ...filter,
      dateStart: explicitDateStart,
      dateEnd: explicitDateEnd,
    }),
    [explicitDateEnd, explicitDateStart, filter],
  );

  useEffect(() => {
    if (userMemoIntent?.state !== "detail" || !userMemoIntent.clearDateFilter) {
      return;
    }
    void setDateFilterQuery({
      memoDate: null,
      memoDateStart: null,
      memoDateEnd: null,
    });
  }, [setDateFilterQuery, userMemoIntent]);

  const queryFilterDateRange = useMemo(() => {
    return panelDateFilterRange({
      ...DEFAULT_FILTER,
      dateStart: effectiveFilter.dateStart,
      dateEnd: effectiveFilter.dateEnd,
    });
  }, [effectiveFilter.dateEnd, effectiveFilter.dateStart]);
  const queryFilter = useMemo(
    () => ({
      deleted: effectiveFilter.deleted,
      visibleDateRange: queryFilterDateRange,
    }),
    [effectiveFilter.deleted, queryFilterDateRange],
  );
  const [{ data: systemMemoLabelsData }] = useQuery({
    query: UserMemoSystemMemoLabelsQuery,
    pause: mode !== "MEMO" || !isWorkspaceSidePanelVisible || !userMemoIntent,
    requestPolicy: "cache-and-network",
  });
  const systemMemoLabels = useMemo<SystemMemoLabelFilterOption[]>(
    () =>
      (systemMemoLabelsData?.systemMemoLabels ?? [])
        .flatMap((label) =>
          label.key
            ? [
                {
                  key: label.key,
                  name: label.name,
                  color: label.color,
                  localizedTexts: label.localizedTexts,
                },
              ]
            : [],
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [systemMemoLabelsData?.systemMemoLabels],
  );
  useEffect(() => {
    if (!systemMemoLabels.length) return;
    const systemMemoLabelSourceFilters = systemMemoLabels.map((label) =>
      memoSourceFilterValueForSystemLabelKey(label.key),
    );
    setFilter((current) => {
      const selectedSourceFilters = new Set(current.sourceFilters);
      if (
        !selectedSourceFilters.has(SYSTEM_MEMO_SOURCE_FILTER_VALUE) ||
        systemMemoLabelSourceFilters.some((source) =>
          selectedSourceFilters.has(source),
        )
      ) {
        return current;
      }
      return {
        ...current,
        sourceFilters: Array.from(
          new Set([...current.sourceFilters, ...systemMemoLabelSourceFilters]),
        ),
      };
    });
  }, [systemMemoLabels]);

  const relatedSystemMemoContexts = useMemo(
    () =>
      relatedSystemMemoContextsFromIntent(
        userMemoIntent ?? null,
        publishedRelatedContexts,
      ),
    [publishedRelatedContexts, userMemoIntent],
  );
  const relatedSystemMemos = useRelatedSystemMemoPanelMemos({
    panelContext: isWorkspaceSidePanelVisible ? context : undefined,
    relatedContexts: relatedSystemMemoContexts,
    queryFilter,
    locale: memoLocale,
  });

  const queryContext = useMemo(
    () =>
      userMemoIntent
        ? memoContextInput(userMemoIntent)
        : emptyMemoQueryContext(),
    [userMemoIntent],
  );

  const [{ data, fetching, error }, reexecutePanelQuery] = useQuery({
    query: MemoPanelQuery,
    variables: {
      context: queryContext,
      filter: queryFilter,
      orderBy: "DATE_DESC" as const,
      locale: memoLocale,
    },
    pause: !context || mode !== "MEMO" || !isWorkspaceSidePanelVisible,
  });

  const [, createUserMemo] = useMutation(CreateUserMemoMutation);
  const [, updateUserMemo] = useMutation(UpdateUserMemoMutation);
  const [, deleteUserMemo] = useMutation(DeleteUserMemoMutation);
  const [, restoreUserMemo] = useMutation(RestoreUserMemoMutation);

  /**
   * 現在の chart / view target にだけ optimistic memo を保持します。
   * target を跨いだ古い optimistic state はここで捨て、別 view の memo が混ざるのを防ぎます。
   */
  const rememberOptimisticMemoForContext = useCallback(
    (targetContext: NonNullable<typeof context>, memo: MemoPanelMemo) => {
      const targetContextKey = memoContextKey(targetContext);
      setOptimisticMemos((current) => {
        const next =
          current.contextKey === targetContextKey
            ? new Map(current.memosById)
            : new Map<string, MemoPanelMemo>();
        next.set(memo.id, memo);
        return { contextKey: targetContextKey, memosById: next };
      });
    },
    [],
  );

  const activeOptimisticMemosById =
    optimisticMemos.contextKey === currentOptimisticMemoContextKey
      ? optimisticMemos.memosById
      : EMPTY_OPTIMISTIC_MEMOS;

  const queryMemos = useMemo(() => {
    const serverMemos = (data?.memos ?? []).filter(isMemoNode);
    const primaryMemos = mergePanelOptimisticMemos(
      serverMemos,
      activeOptimisticMemosById,
    );
    const memosById = new Map(
      primaryMemos.map((memo) => [memo.id, memo] as const),
    );
    for (const memo of relatedSystemMemos.memos) {
      memosById.set(memo.id, memo);
    }
    return Array.from(memosById.values()).sort(compareMemoPanelMemosByDateDesc);
  }, [activeOptimisticMemosById, data, relatedSystemMemos.memos]);

  useEffect(() => {
    if (
      !data?.memos ||
      activeOptimisticMemosById.size === 0 ||
      optimisticMemos.contextKey !== currentOptimisticMemoContextKey
    ) {
      return;
    }
    const serverMemoIds = new Set(
      data.memos.filter(isMemoNode).map((memo) => memo.id),
    );
    setOptimisticMemos((current) => {
      if (current.contextKey !== currentOptimisticMemoContextKey)
        return current;
      const next = new Map(current.memosById);
      for (const memoId of serverMemoIds) {
        next.delete(memoId);
      }
      return next.size === current.memosById.size
        ? current
        : { contextKey: current.contextKey, memosById: next };
    });
  }, [
    activeOptimisticMemosById.size,
    currentOptimisticMemoContextKey,
    data,
    optimisticMemos.contextKey,
  ]);

  const availableAuthors = useMemo(
    () => authorsFromMemosForFilter(queryMemos),
    [queryMemos],
  );

  const memos = useMemo(
    () =>
      userMemosMatchingPanelDerivedFilters(queryMemos, effectiveFilter, {
        hasSystemMemoLabelOptions: systemMemoLabels.length > 0,
      }),
    [effectiveFilter, queryMemos, systemMemoLabels.length],
  );

  const selectedMemo = useMemo(() => {
    if (
      !userMemoIntent ||
      userMemoIntent.state === "list" ||
      userMemoIntent.state === "create"
    ) {
      return null;
    }
    return queryMemos.find((memo) => memo.id === userMemoIntent.memoId) ?? null;
  }, [queryMemos, userMemoIntent]);

  const canUseUserMemoEditActions = useMemo(() => {
    if (!context?.featureKind) return false;
    return checkPolicy(userMemoEditPolicyForFeatureKind(context.featureKind))
      .ok;
  }, [checkPolicy, context?.featureKind]);
  const selectedCreateTargetKey =
    userMemoIntent?.state === "create"
      ? (activeDraftForm?.createTargetKey ??
        memoContextKey(userMemoIntent.context))
      : userMemoIntent?.state === "edit" || userMemoIntent?.state === "detail"
        ? (activeDraftForm?.createTargetKey ??
          memoTargetOptionKeyFromMemo(
            selectedMemo,
            userMemoIntent.context,
            createTargetOptions,
          ))
        : null;
  const selectedCreateTargetContext = useMemo(() => {
    if (
      userMemoIntent?.state !== "create" &&
      userMemoIntent?.state !== "edit" &&
      userMemoIntent?.state !== "detail"
    ) {
      return context ?? null;
    }
    return (
      createTargetOptions.find(
        (option) => option.key === selectedCreateTargetKey,
      )?.context ?? userMemoIntent.context
    );
  }, [context, createTargetOptions, selectedCreateTargetKey, userMemoIntent]);

  const refreshPanel = () => {
    reexecutePanelQuery({ requestPolicy: "cache-and-network" });
    relatedSystemMemos.refresh();
  };

  const openMemoListForContext = useCallback(
    (
      nextContext: NonNullable<typeof context>,
      dateRange = panelDateFilterRange(visibleFilter),
      relatedContexts?: UserMemoContext[],
    ) => {
      openUserMemo({
        state: "list",
        context: nextContext,
        relatedContexts,
        dateStart: dateRange?.startDate,
        dateEnd:
          dateRange && dateRange.endDate !== dateRange.startDate
            ? dateRange.endDate
            : undefined,
      });
    },
    [openUserMemo, visibleFilter],
  );

  const openMemoList = useCallback(
    (dateRange = panelDateFilterRange(visibleFilter)) => {
      if (!userMemoIntent) return;
      if (userMemoIntent.state === "detail" && userMemoIntent.returnList) {
        openMemoListForContext(
          userMemoIntent.returnList.context,
          dateRange,
          userMemoIntent.returnList.relatedContexts,
        );
        return;
      }
      openMemoListForContext(userMemoIntent.context, dateRange);
    },
    [openMemoListForContext, userMemoIntent, visibleFilter],
  );

  const closeMemoPanel = useCallback(() => {
    void setDateFilterQuery({
      memoDate: null,
      memoDateStart: null,
      memoDateEnd: null,
    });
    closeWorkspaceSidePanel();
  }, [closeWorkspaceSidePanel, setDateFilterQuery]);

  const handleFilterChange = useCallback(
    (nextFilter: PanelFilter) => {
      if (
        nextFilter.dateStart !== explicitDateStart ||
        nextFilter.dateEnd !== explicitDateEnd
      ) {
        void setDateFilterQuery(
          nextFilter.dateStart
            ? {
                memoDate: null,
                memoDateStart: nextFilter.dateStart,
                memoDateEnd: nextFilter.dateEnd || null,
              }
            : {
                memoDate: null,
                memoDateStart: null,
                memoDateEnd: null,
              },
        );
      }
      setFilter({
        ...nextFilter,
        dateStart: DEFAULT_FILTER.dateStart,
        dateEnd: DEFAULT_FILTER.dateEnd,
      });
    },
    [explicitDateEnd, explicitDateStart, setDateFilterQuery],
  );

  const handleFilterQueryChange = useCallback((query: string) => {
    setFilter((current) => ({ ...current, query }));
  }, []);

  /**
   * side panel 自身と chart 側 memo を同時に再取得させます。
   * mutation 結果だけでは別 query の chart props が更新されないため、target view 単位で通知します。
   */
  const refreshUserMemoSurfaces = (
    publicationScopes?: readonly MemoPublicationScope[],
    refreshContext = userMemoIntent?.context,
  ) => {
    if (!refreshContext) return;
    refreshPanel();
    dispatchMemoChanged(refreshContext, {
      publicationScopes,
    });
  };

  const handleAdd = () => {
    if (!userMemoIntent || !canUseUserMemoEditActions) return;
    const nextIntent: MemoPanelIntent = {
      state: "create",
      context: userMemoIntent.context,
      initialDate: effectiveFilter.dateStart || undefined,
      source: "panel",
    };
    setDraftForm({
      ownerKey: memoFormDraftOwnerKeyFromIntent(nextIntent) ?? "",
      form: formStateFromIntent(nextIntent, DEFAULT_MEMO_COLOR),
    });
    setFormError(null);
    openUserMemo(nextIntent);
  };

  const highlightChartMemoFromList = useCallback(
    (memo: MemoPanelMemo) => {
      if (!userMemoIntent) return;
      setListHoveredMemoId(memo.id);
      dispatchMemoHovered({
        context: userMemoIntent.context,
        targetId: userMemoIntent.context.targetId,
        source: "list",
        memoIds: [memo.id],
        bucket: memo.startDate ?? null,
        startDate: memo.startDate ?? null,
        endDate: memo.endDate ?? null,
      });
    },
    [userMemoIntent],
  );

  const clearChartMemoFromList = useCallback(() => {
    if (!userMemoIntent) return;
    setListHoveredMemoId(null);
    dispatchMemoHovered({
      context: userMemoIntent.context,
      targetId: userMemoIntent.context.targetId,
      source: "list",
      memoIds: [],
      bucket: null,
      startDate: null,
      endDate: null,
    });
  }, [userMemoIntent]);

  const handleFormChange = (nextForm: MemoFormState) => {
    if (!currentDraftOwnerKey) return;
    setDraftForm({ ownerKey: currentDraftOwnerKey, form: nextForm });
    setFormError(null);
  };

  const handleCreateTargetChange = (targetKey: string) => {
    if (!currentDraftOwnerKey || !userMemoIntent) return;
    setDraftForm({
      ownerKey: currentDraftOwnerKey,
      form: {
        ...(activeDraftForm ??
          formStateFromIntent(userMemoIntent, DEFAULT_MEMO_COLOR)),
        createTargetKey: targetKey,
      },
    });
    setFormError(null);
  };

  const handleSave = async () => {
    if (
      !userMemoIntent ||
      !activeDraftForm ||
      (userMemoIntent.state !== "create" &&
        userMemoIntent.state !== "edit" &&
        userMemoIntent.state !== "detail")
    ) {
      return;
    }
    if (isSavingMemo) {
      return;
    }
    // 日付の順序 validation も一覧 filter も日付を文字列比較するため、正規化は validation より前に行います。
    const normalizedDraftForm = {
      ...activeDraftForm,
      startDate: normalizeMemoDateInput(activeDraftForm.startDate),
      endDate: normalizeMemoDateInput(activeDraftForm.endDate),
    };
    const validationError = memoFormErrorMessage(normalizedDraftForm, i18n, {
      requireDate:
        userMemoIntent.state === "create" &&
        userMemoIntent.context.featureKind === "SEARCH_ANALYTICS",
    });
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const inferredDateMode = memoFormDateModeFromDates(normalizedDraftForm);
    const dateFields =
      inferredDateMode === "NONE"
        ? { startDate: null, endDate: null }
        : inferredDateMode === "DAY"
          ? { startDate: normalizedDraftForm.startDate, endDate: null }
          : {
              startDate: normalizedDraftForm.startDate,
              endDate: normalizedDraftForm.endDate,
            };
    const listDateRangeAfterSave =
      userMemoIntent.state === "create" && userMemoIntent.source === "chart"
        ? panelDateFilterRange({
            ...DEFAULT_FILTER,
            dateStart: dateFields.startDate ?? "",
            dateEnd: dateFields.endDate ?? "",
          })
        : undefined;

    const publicationScopesForRefresh: MemoPublicationScope[] = [
      activeDraftForm.publicationScope,
    ];
    if (
      (userMemoIntent.state === "edit" || userMemoIntent.state === "detail") &&
      selectedMemo?.publicationScope &&
      selectedMemo.publicationScope !== activeDraftForm.publicationScope
    ) {
      publicationScopesForRefresh.push(selectedMemo.publicationScope);
    }

    const mutationContext =
      activeDraftForm.publicationScope === "VIEW"
        ? (selectedCreateTargetContext ?? userMemoIntent.context)
        : userMemoIntent.context;
    const previousOriginContext =
      selectedMemo?.originTargetKind && selectedMemo.originTargetID
        ? {
            ...userMemoIntent.context,
            targetKind: selectedMemo.originTargetKind,
            targetId: selectedMemo.originTargetID,
          }
        : null;
    const baseInput = {
      context: memoContextFromUserMemoContext(mutationContext),
      title: activeDraftForm.title.trim(),
      body: activeDraftForm.body.trim(),
      color: activeDraftForm.color,
      dateMode: inferredDateMode,
      publicationScope: activeDraftForm.publicationScope,
      ...dateFields,
    };

    setIsSavingMemo(true);
    try {
      if (
        userMemoIntent.state === "edit" ||
        userMemoIntent.state === "detail"
      ) {
        const targetMemoId =
          userMemoIntent.state === "edit"
            ? userMemoIntent.memoId
            : selectedMemo?.id;
        if (!targetMemoId) return;
        const result = await updateUserMemo({
          input: { id: targetMemoId, ...baseInput },
        });
        if (result.error) {
          toast({
            title: i18n._(msg`Failed to save memo`),
            description: result.error.message,
            variant: "destructive",
          });
          return;
        }
        const updatedMemo = result.data?.updateUserMemo;
        if (isMemoNode(updatedMemo)) {
          rememberOptimisticMemoForContext(mutationContext, updatedMemo);
          setDraftForm({
            ownerKey: memoFormDraftOwnerKeyFromMemoId(updatedMemo.id),
            form: formStateFromMemo(
              updatedMemo,
              DEFAULT_MEMO_COLOR,
              memoContextKey(mutationContext),
            ),
          });
        }
      } else {
        const result = await createUserMemo({ input: baseInput });
        if (result.error) {
          toast({
            title: i18n._(msg`Failed to save memo`),
            description: result.error.message,
            variant: "destructive",
          });
          return;
        }
        const createdMemo = result.data?.createUserMemo;
        if (isMemoNode(createdMemo)) {
          rememberOptimisticMemoForContext(mutationContext, createdMemo);
          setDraftForm({
            ownerKey: memoFormDraftOwnerKeyFromMemoId(createdMemo.id),
            form: formStateFromMemo(
              createdMemo,
              DEFAULT_MEMO_COLOR,
              memoContextKey(mutationContext),
            ),
          });
        }
      }
    } finally {
      setIsSavingMemo(false);
    }
    refreshUserMemoSurfaces(publicationScopesForRefresh, mutationContext);
    if (
      memoContextKey(mutationContext) !== memoContextKey(userMemoIntent.context)
    ) {
      dispatchMemoChanged(userMemoIntent.context, {
        publicationScopes: publicationScopesForRefresh,
      });
    }
    if (
      previousOriginContext &&
      memoContextKey(previousOriginContext) !==
        memoContextKey(mutationContext) &&
      memoContextKey(previousOriginContext) !==
        memoContextKey(userMemoIntent.context)
    ) {
      dispatchMemoChanged(previousOriginContext, {
        publicationScopes: publicationScopesForRefresh,
      });
    }
    openMemoListForContext(mutationContext, listDateRangeAfterSave);
  };

  const handleDelete = async (memo: MemoPanelMemo) => {
    if (!userMemoIntent) return;
    const result = await deleteUserMemo({ input: { id: memo.id } });
    if (result.error) {
      toast({
        title: i18n._(msg`Failed to archive memo`),
        description: result.error.message,
        variant: "destructive",
      });
      return;
    }
    refreshUserMemoSurfaces([memo.publicationScope]);
    openMemoList();
  };

  const handleRestore = async (memo: MemoPanelMemo) => {
    if (!userMemoIntent) return;
    const result = await restoreUserMemo({ input: { id: memo.id } });
    if (result.error) {
      toast({
        title: i18n._(msg`Failed to unarchive memo`),
        description: result.error.message,
        variant: "destructive",
      });
      return;
    }
    const restoredMemo = result.data?.restoreUserMemo;
    if (isMemoNode(restoredMemo)) {
      rememberOptimisticMemoForContext(userMemoIntent.context, restoredMemo);
      setDraftForm({
        ownerKey: memoFormDraftOwnerKeyFromMemoId(restoredMemo.id),
        form: formStateFromMemo(restoredMemo, DEFAULT_MEMO_COLOR),
      });
    }
    refreshUserMemoSurfaces([
      restoredMemo?.publicationScope ?? memo.publicationScope,
    ]);
    openUserMemo({
      state: "detail",
      context: userMemoIntent.context,
      memoId: restoredMemo?.id ?? memo.id,
    });
  };

  const openMemoDetail = (memo: MemoPanelMemo) => {
    const detailContext =
      relatedSystemMemos.memoContextById.get(memo.id) ?? context;
    if (!detailContext) return;
    clearChartMemoFromList();
    setDraftForm({
      ownerKey: memoFormDraftOwnerKeyFromMemoId(memo.id),
      form: formStateFromMemo(memo, DEFAULT_MEMO_COLOR),
    });
    setFormError(null);
    rememberOptimisticMemoForContext(detailContext, memo);
    const currentListRelatedContexts =
      userMemoIntent?.state === "list"
        ? Array.from(
            new Map(
              [
                ...(userMemoIntent.relatedContexts ?? []),
                ...publishedRelatedContexts,
              ].map(
                (relatedContext) =>
                  [memoContextKey(relatedContext), relatedContext] as const,
              ),
            ).values(),
          )
        : [];
    const shouldPreserveReturnList =
      userMemoIntent?.state === "list" &&
      (memoContextKey(detailContext) !==
        memoContextKey(userMemoIntent.context) ||
        currentListRelatedContexts.length > 0);
    const returnList = shouldPreserveReturnList
      ? {
          context: userMemoIntent.context,
          relatedContexts:
            currentListRelatedContexts.length > 0
              ? currentListRelatedContexts
              : undefined,
        }
      : userMemoIntent?.state === "detail"
        ? userMemoIntent.returnList
        : undefined;
    openUserMemo({
      state: "detail",
      context: detailContext,
      memoId: memo.id,
      returnList,
    });
  };

  const createOrEditForm = userMemoIntent
    ? (activeDraftForm ??
      formStateFromIntent(userMemoIntent, DEFAULT_MEMO_COLOR))
    : null;
  const selectedMemoForm = selectedMemo
    ? (activeDraftForm ?? formStateFromMemo(selectedMemo, DEFAULT_MEMO_COLOR))
    : null;
  const selectedMemoCanEdit = Boolean(
    selectedMemo?.canEdit && !selectedMemo.deletedAt,
  );

  const panelTitle = !userMemoIntent
    ? i18n._(msg`Memo list`)
    : userMemoIntent.state === "create"
      ? i18n._(msg`Create memo`)
      : userMemoIntent.state === "edit" || userMemoIntent.state === "detail"
        ? selectedMemoCanEdit
          ? i18n._(msg`Edit memo`)
          : i18n._(msg`Memo details`)
        : i18n._(msg`Memo list`);

  return {
    availableAuthors,
    canUseUserMemoEditActions,
    chartHoveredMemoIds,
    clearChartMemoFromList,
    closeMemoPanel,
    context,
    createOrEditForm,
    createTargetOptions,
    errorMessage: error?.message,
    fetching: fetching || relatedSystemMemos.fetching,
    filter: visibleFilter,
    formError,
    handleAdd,
    handleDelete,
    handleFilterChange,
    handleFilterQueryChange,
    handleCreateTargetChange,
    handleFormChange,
    handleRestore,
    handleSave,
    highlightChartMemoFromList,
    isSavingMemo,
    listHoveredMemoId,
    memos,
    openMemoDetail,
    openMemoList,
    panelTitle,
    selectedMemo,
    selectedMemoCanEdit,
    selectedMemoForm,
    selectedCreateTargetKey,
    systemMemoLabels,
    memoDisplaySettings,
    userMemoIntent,
    writeMemoDisplaySettings: writeMemoDisplaySettings,
  };
}
